/**
 * Turn a folder of exports into a list of runs to analyse, or a reason not to.
 *
 * Pure: detection has already happened (in the batch worker, one file at a
 * time), so this decides only what the identified files MEAN together. That
 * separation is what makes the interesting rules testable without parsing
 * megabytes.
 *
 * Three rules, all of them refusals rather than guesses:
 *
 * 1. **A pair is two runs of ONE source.** Files are grouped by the profile
 *    detected from their records, never by name or folder order. A file no
 *    profile matched, or one that matched two, is skipped and named — batch is
 *    the wrong place to resolve an ambiguity a person has not seen.
 * 2. **Order comes from the pipeline's filenames or not at all.** Getting
 *    reference and candidate the wrong way round inverts every finding in the
 *    run. On the upload screen the export dates inside the files can settle it;
 *    here, where nobody is watching each pair, an unorderable group is skipped
 *    with a message pointing at the screen that can.
 * 3. **The two newest runs, and it says so.** A source with five runs in the
 *    folder gets its two newest compared — the question a batch answers is
 *    "what changed in the latest run" — and the plan states which files those
 *    were, so the choice is visible rather than implied.
 */

import { parseRunFilename, type RunFilename } from "../../engine/run-filename";

/** One file, with whatever detection concluded about it. */
export type DetectedFile = {
  fileName: string;
  bytes: number;
  /** The profile detected from the records, or null for none/ambiguous. */
  profileId: string | null;
  /** Why there is no profile id, for the skip list. Absent when there is one. */
  detectionNote?: string;
};

export type PlannedPair = {
  profileId: string;
  referenceFileName: string;
  candidateFileName: string;
  /** Combined size of the two files, for the size guardrail. */
  bytes: number;
  /** Runs of this source in the folder; more than two means the rest were left. */
  runsAvailable: number;
};

export type SkippedGroup = {
  /** The profile the files belong to, or null when detection found none. */
  profileId: string | null;
  fileNames: string[];
  reason: string;
};

export type BatchPlan = {
  pairs: PlannedPair[];
  skipped: SkippedGroup[];
};

export function planBatch(files: DetectedFile[]): BatchPlan {
  const pairs: PlannedPair[] = [];
  const skipped: SkippedGroup[] = [];

  const undetected = files.filter((file) => file.profileId === null);
  if (undetected.length > 0) {
    // Grouped by the reason, so a folder of ten unrecognized files reads as one
    // line rather than ten.
    const byReason = new Map<string, string[]>();
    for (const file of undetected) {
      const reason = file.detectionNote ?? "No known source matched this file.";
      byReason.set(reason, [...(byReason.get(reason) ?? []), file.fileName]);
    }
    for (const [reason, fileNames] of byReason) {
      skipped.push({
        profileId: null,
        fileNames: [...fileNames].sort(),
        reason: `${reason} A batch run cannot pick a profile for you — analyse these on the upload screen.`
      });
    }
  }

  const bySource = new Map<string, DetectedFile[]>();
  for (const file of files) {
    if (file.profileId === null) continue;
    bySource.set(file.profileId, [...(bySource.get(file.profileId) ?? []), file]);
  }

  for (const profileId of [...bySource.keys()].sort()) {
    const group = bySource.get(profileId)!;
    if (group.length < 2) {
      skipped.push({
        profileId,
        fileNames: group.map((file) => file.fileName),
        reason: "Only one run of this source is in the folder, and a comparison needs two."
      });
      continue;
    }

    const dated: Array<{ file: DetectedFile; run: RunFilename }> = [];
    const undatable: DetectedFile[] = [];
    for (const file of group) {
      const run = parseRunFilename(file.fileName);
      if (run === null) undatable.push(file);
      else dated.push({ file, run });
    }

    if (dated.length < 2) {
      skipped.push({
        profileId,
        fileNames: group.map((file) => file.fileName).sort(),
        reason:
          "These filenames do not carry the pipeline's run timestamps, so which run is the reference " +
          "cannot be established here. Load them on the upload screen, where the export dates inside " +
          "the files settle the order."
      });
      continue;
    }

    // Newest last: startAt is fixed-width, so lexicographic order is chronological.
    dated.sort(
      (left, right) => left.run.startedAt.localeCompare(right.run.startedAt) || left.run.runId.localeCompare(right.run.runId)
    );
    const candidate = dated[dated.length - 1]!;
    const reference = dated[dated.length - 2]!;

    pairs.push({
      profileId,
      referenceFileName: reference.file.fileName,
      candidateFileName: candidate.file.fileName,
      bytes: reference.file.bytes + candidate.file.bytes,
      runsAvailable: dated.length
    });

    if (undatable.length > 0) {
      skipped.push({
        profileId,
        fileNames: undatable.map((file) => file.fileName).sort(),
        reason: "Left out of this source's pair: the filename carries no run timestamp to order it by."
      });
    }
  }

  return { pairs, skipped };
}
